import React, { useEffect, useState, useMemo } from "react";
import { CalendarDays, Download, Rss, Copy, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const TYPE_COLOR = {
  license: "bg-dox-orange", insurance: "bg-emerald-400", tax: "bg-dox-magenta",
  annual_report: "bg-dox-purple", permit: "bg-yellow-400", bond: "bg-sky-400", filing: "bg-white/60",
};

function parseDate(s) {
  const fmts = [s, `${s}T00:00:00`];
  for (const f of fmts) { const d = new Date(f); if (!isNaN(d)) return d; }
  return null;
}

export default function ComplianceCalendar() {
  const [events, setEvents] = useState([]);
  const [sub, setSub] = useState(null);
  const [cursor, setCursor] = useState(() => new Date());

  useEffect(() => {
    api.get("/calendar/events").then(({ data }) => setEvents(data)).catch(() => {});
    api.get("/calendar/subscribe").then(({ data }) => setSub(data)).catch(() => {});
  }, []);

  const byDay = useMemo(() => {
    const m = {};
    events.forEach((e) => { const d = parseDate(e.date); if (d) { const k = d.toDateString(); (m[k] = m[k] || []).push(e); } });
    return m;
  }, [events]);

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const exportUrl = `${API}/calendar/export.ics?auth=${localStorage.getItem("ezdox_token")}`;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied — paste into your calendar app to subscribe."); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display font-black text-2xl flex items-center gap-2"><CalendarDays size={22} className="text-dox-orange" /> Compliance Calendar</h2>
          <p className="text-white/55 text-sm mt-0.5">Dox schedules every renewal, filing and expiration automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={exportUrl} data-testid="calendar-export" className="rounded-full glass px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-white/10"><Download size={15} /> Export .ics</a>
          {sub && <a href={sub.google_url} target="_blank" rel="noreferrer" className="rounded-full glass px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-white/10"><Rss size={15} /> Google</a>}
        </div>
      </div>

      {sub && (
        <div className="glass-card p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="text-sm text-white/60 flex-1">
            <span className="font-semibold text-white">Sync with Apple / Outlook / Google:</span> subscribe to your private feed for auto-updating reminders.
            <div className="font-mono text-xs text-white/40 mt-1 truncate">{sub.webcal_url}</div>
          </div>
          <button onClick={() => copy(sub.webcal_url)} data-testid="calendar-copy-feed" className="rounded-full bg-dox-orange px-4 py-2 text-sm font-bold flex items-center gap-2 hover:bg-dox-orangeHover shrink-0"><Copy size={14} /> Copy feed URL</button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold text-lg">{first.toLocaleString("default", { month: "long", year: "numeric" })}</div>
            <div className="flex gap-1">
              <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="h-8 w-8 rounded-lg glass flex items-center justify-center hover:bg-white/10"><ChevronLeft size={16} /></button>
              <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="h-8 w-8 rounded-lg glass flex items-center justify-center hover:bg-white/10"><ChevronRight size={16} /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-white/40 mb-2">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              const evs = d ? (byDay[d.toDateString()] || []) : [];
              const isToday = d && d.toDateString() === new Date().toDateString();
              return (
                <div key={i} className={`aspect-square rounded-lg p-1.5 text-xs flex flex-col ${d ? "bg-white/[0.02] border border-white/5" : ""} ${isToday ? "ring-1 ring-dox-orange" : ""}`}>
                  {d && <span className="text-white/60">{d.getDate()}</span>}
                  <div className="mt-auto flex flex-wrap gap-0.5">
                    {evs.slice(0, 3).map((e, j) => <span key={j} title={e.title} className={`h-1.5 w-1.5 rounded-full ${TYPE_COLOR[e.type] || "bg-white/50"}`} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="glass-card p-5">
          <h3 className="font-display font-bold text-base mb-3">Upcoming</h3>
          {events.length === 0 ? <p className="text-white/40 text-sm">No scheduled deadlines yet.</p> : (
            <ul className="space-y-3">
              {events.slice(0, 10).map((e) => (
                <li key={e.id} data-testid={`cal-event-${e.id}`} className="flex items-start gap-3">
                  <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${TYPE_COLOR[e.type] || "bg-white/50"}`} />
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{e.title}</div>
                    <div className="text-xs text-white/40 font-mono">{e.date}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
